import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Wifi, Activity, GaugeCircle, Settings, Cpu, CircuitBoard, Thermometer, Fan, FileText, Wrench } from "lucide-react";

export default function GatewaysView() {
  const [activeTab, setActiveTab] = useState("overview");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const tabs = [
    { id: "overview", label: "Device Overview", icon: GaugeCircle },
    { id: "connectivity", label: "Connectivity", icon: Wifi },
    { id: "modbus", label: "Modbus RTU", icon: CircuitBoard },
    { id: "analog", label: "Analog Inputs", icon: Activity },
    { id: "digital", label: "Digital Inputs", icon: Cpu },
    { id: "thermal", label: "Temperature & Fan", icon: Thermometer },
    { id: "diagnostics", label: "Diagnostics & Logs", icon: FileText },
  ];

  const Placeholder = ({ title, description, children }) => (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold">{title}</h2>
        <Badge variant="outline">Live</Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{description}</p>
      <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground bg-muted/20">
        {children}
      </div>
    </Card>
  );

  return (
    <div className="relative flex flex-col">
      <Card className="p-4 rounded-none border-b border-gray-200 bg-white">
        <div className="container mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">IoT Gateway Dashboard</h1>
            <p className="text-gray-600 text-sm">Real-time telemetry and capability-driven UI</p>
          </div>
          <Button onClick={() => setSettingsOpen(true)} variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
        </div>
      </Card>

      <div className="container mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b border-gray-200 bg-white/50">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-4 md:grid-cols-7 bg-transparent h-auto p-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex flex-col items-center gap-1 py-3 text-xs sm:text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-b-2 data-[state=active]:border-blue-600"
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="py-4 space-y-4">
            <TabsContent value="overview" className="m-0">
              <Placeholder
                title="Device Overview"
                description="Identity, firmware, IP, connection status, last-seen"
              >
                Display gateway identity, firmware version, LAN IP, connection state, and last heartbeat.
              </Placeholder>
            </TabsContent>

            <TabsContent value="connectivity" className="m-0">
              <Placeholder
                title="Connectivity"
                description="LAN/GSM mode, signal, usage, failover history"
              >
                Show active interface, RSSI/RSRP, data counters, and recent failover events.
              </Placeholder>
            </TabsContent>

            <TabsContent value="modbus" className="m-0">
              <Placeholder
                title="Modbus RTU"
                description="Live registers, polling control, diagnostics"
              >
                Render live register table, start/stop polling, and show error counters/timeouts.
              </Placeholder>
            </TabsContent>

            <TabsContent value="analog" className="m-0">
              <Placeholder
                title="Analog Inputs"
                description="Real-time charts, calibration, thresholds"
              >
                Graph AI channels with calibration options and alert thresholds.
              </Placeholder>
            </TabsContent>

            <TabsContent value="digital" className="m-0">
              <Placeholder
                title="Digital Inputs"
                description="Binary states and timestamped event logs"
              >
                Display DI states and event list with timestamps.
              </Placeholder>
            </TabsContent>

            <TabsContent value="thermal" className="m-0">
              <Placeholder
                title="Temperature & Fan Control"
                description="Thermal monitoring and auto/manual cooling"
              >
                Show temperature sensors, fan RPM, and controls for auto/manual modes.
              </Placeholder>
            </TabsContent>

            <TabsContent value="diagnostics" className="m-0">
              <Placeholder
                title="Diagnostics & Logs"
                description="System events, transitions, errors, export"
              >
                Provide system log stream with filters and export to file.
              </Placeholder>
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Settings Drawer (Sheet) */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Gateway Settings</SheetTitle>
            <SheetDescription>Remote configuration, APN/network settings, RBAC</SheetDescription>
          </SheetHeader>
          <div className="p-4 space-y-3">
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground bg-muted/20">
              Settings form goes here (APN, network modes, roles)
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setSettingsOpen(false)} size="sm" variant="outline">
                Close
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}


